import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SidBarClient from "./SideBarClient";

/* ─── Types ────────────────────────────────────────────── */
interface Ticket {
  id: number;
  IdClient?: number;
  NamUtilisateur: string;
  Probleme: string;
  Description: string;
  Status: string;
  Date: string;
}
interface ChatMsg {
  id: number;
  IdTicket: number;
  NamUtilisateur: string;
  Message: string;
  sender_type: "client" | "magasin";
  Date: string;
}

/* ─── Status design tokens ──────────────────────────────── */
type SK = "Non lu" | "Lu" | "Résolu" | "Fermé";
const STATUS: Record<SK, { dot: string; pill: string; text: string }> = {
  "Non lu": { dot: "#D97706", pill: "#FEF3C7", text: "#92400E" },
  "Lu":     { dot: "#4F46E5", pill: "#EEF2FF", text: "#3730A3" },
  "Résolu": { dot: "#059669", pill: "#D1FAE5", text: "#065F46" },
  "Fermé":  { dot: "#64748B", pill: "#F1F5F9", text: "#475569" },
};
const getS = (s: string) => STATUS[s as SK] ?? STATUS["Fermé"];

const fmt = (d: string) =>
  new Date(d).toLocaleString("fr-FR", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });

const initial = (name?: string) => (name?.trim()?.[0] ?? "?").toUpperCase();

/* ─── Design system + CSS ───────────────────────────────── */
const CSS = `
.mtc-root{
  /* ── Design tokens ── */
  --bg:#F7F9FC; --surface:#FFFFFF; --ink:#0F172A; --ink-2:#475569; --muted:#94A3B8;
  --line:#E7EBF1; --line-strong:#CBD5E1;
  --brand:#0B1220; --accent:#D97706; --focus:#4F46E5;
  --radius-sm:8px; --radius:12px; --radius-lg:16px;
  --shadow-1:0 1px 2px rgba(15,23,42,.04),0 1px 3px rgba(15,23,42,.06);
  --shadow-2:0 4px 12px rgba(15,23,42,.06),0 2px 4px rgba(15,23,42,.04);
  --shadow-3:0 12px 32px rgba(15,23,42,.10);
  --ring:0 0 0 3px rgba(79,70,229,.18);
  --font:'Inter',system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;

  font-family:var(--font);
  background:var(--bg);
  min-height:100vh;
  color:var(--ink);
  -webkit-font-smoothing:antialiased;
  text-rendering:optimizeLegibility;
}
.mtc-root *,.mtc-root *::before,.mtc-root *::after{box-sizing:border-box}
.mtc-page{max-width:920px;margin:0 auto;padding:40px 24px 96px}

/* ── Header ── */
.mtc-hdr{display:flex;align-items:center;gap:14px;margin-bottom:32px}
.mtc-back{width:38px;height:38px;border-radius:var(--radius-sm);border:1px solid var(--line);
  background:var(--surface);display:flex;align-items:center;justify-content:center;cursor:pointer;
  color:var(--ink-2);box-shadow:var(--shadow-1);transition:background .15s,color .15s,border-color .15s,transform .15s}
.mtc-back:hover{background:var(--brand);border-color:var(--brand);color:#fff}
.mtc-back:active{transform:scale(.96)}
.mtc-back:focus-visible{outline:none;box-shadow:var(--ring)}
.mtc-title{font-size:1.5rem;font-weight:800;letter-spacing:-.02em;line-height:1.1}
.mtc-sub{font-size:.82rem;color:var(--muted);margin-top:3px}

/* ── KPI cards ── */
.mtc-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:28px}
@media(max-width:560px){.mtc-kpis{grid-template-columns:repeat(2,1fr)}}
.mtc-kpi{background:var(--surface);border:1px solid var(--line);border-radius:var(--radius);
  padding:16px 18px;position:relative;overflow:hidden;box-shadow:var(--shadow-1);
  transition:transform .18s ease,box-shadow .18s ease,border-color .18s ease}
.mtc-kpi:hover{transform:translateY(-2px);box-shadow:var(--shadow-2);border-color:var(--line-strong)}
.mtc-kpi-bar{position:absolute;top:0;left:0;right:0;height:3px}
.mtc-kpi-lbl{font-size:.68rem;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--muted)}
.mtc-kpi-val{font-size:1.9rem;font-weight:800;margin-top:6px;letter-spacing:-.02em;line-height:1;
  font-variant-numeric:tabular-nums}

/* ── Tabs ── */
.mtc-tabs{display:flex;gap:6px;margin-bottom:16px;flex-wrap:wrap}
.mtc-tab{padding:7px 14px;border-radius:var(--radius-sm);font-size:.8rem;font-weight:600;cursor:pointer;
  border:1px solid transparent;background:transparent;color:var(--ink-2);
  transition:background .15s,color .15s,border-color .15s;display:inline-flex;align-items:center;gap:7px}
.mtc-tab:hover{background:var(--surface);border-color:var(--line);color:var(--ink)}
.mtc-tab:focus-visible{outline:none;box-shadow:var(--ring)}
.mtc-tab.active{background:var(--brand);color:#fff;border-color:var(--brand);box-shadow:var(--shadow-1)}
.mtc-badge{font-size:.66rem;font-weight:700;padding:1px 7px;border-radius:20px;
  background:#EDF1F7;color:var(--ink-2);font-variant-numeric:tabular-nums}
.mtc-tab.active .mtc-badge{background:rgba(255,255,255,.16);color:#fff}

/* ── Ticket list ── */
.mtc-list{display:flex;flex-direction:column;gap:10px}
.mtc-row{background:var(--surface);border:1px solid var(--line);border-radius:var(--radius);
  overflow:hidden;box-shadow:var(--shadow-1);transition:border-color .18s,box-shadow .18s}
.mtc-row:hover{border-color:var(--line-strong);box-shadow:var(--shadow-2)}
.mtc-row.active-row{border-color:var(--focus);box-shadow:var(--ring)}
.mtc-row-head{display:grid;grid-template-columns:46px 1fr auto;align-items:center;min-height:64px;cursor:pointer}
.mtc-row-head:focus-visible{outline:none;box-shadow:inset var(--ring)}
.mtc-strip{width:46px;align-self:stretch;display:flex;align-items:center;justify-content:center;
  border-right:1px solid var(--line);flex-shrink:0}
.mtc-dot{width:9px;height:9px;border-radius:50%}
.mtc-info{padding:12px 16px;min-width:0}
.mtc-prob{font-size:.88rem;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;letter-spacing:-.01em}
.mtc-meta{display:flex;gap:8px;margin-top:4px;flex-wrap:wrap;align-items:center}
.mtc-id{font-size:.68rem;font-weight:700;color:var(--ink-2);background:#F1F5F9;padding:1px 7px;border-radius:5px;
  font-variant-numeric:tabular-nums}
.mtc-date{font-size:.72rem;color:var(--muted)}
.mtc-right{display:flex;align-items:center;gap:10px;padding:0 16px;flex-shrink:0}
.mtc-pill{display:inline-flex;align-items:center;gap:6px;padding:4px 11px;border-radius:20px;
  font-size:.7rem;font-weight:700;white-space:nowrap}
.mtc-pill-dot{width:5px;height:5px;border-radius:50%}
.mtc-chev{transition:transform .22s ease,color .22s;color:var(--muted)}
.active-row .mtc-chev{transform:rotate(180deg);color:var(--focus)}

/* ── Chat panel ── */
.mtc-chat{border-top:1px solid var(--line);background:var(--bg);display:flex;flex-direction:column;max-height:500px;
  animation:mtc-expand .28s ease}
.mtc-chat-win{flex:1;overflow-y:auto;padding:18px 20px;display:flex;flex-direction:column;gap:12px;scroll-behavior:smooth}
.mtc-chat-win::-webkit-scrollbar{width:6px}
.mtc-chat-win::-webkit-scrollbar-thumb{background:var(--line-strong);border-radius:6px}
.mtc-chat-win::-webkit-scrollbar-track{background:transparent}

/* initial description */
.mtc-init-lbl{font-size:.64rem;font-weight:700;text-transform:uppercase;letter-spacing:.08em;
  color:var(--focus);margin-bottom:6px}
.mtc-init{align-self:flex-start;background:#EEF2FF;border:1px solid #C7D2FE;
  border-radius:4px 14px 14px 14px;padding:11px 15px;max-width:78%;font-size:.86rem;color:#3730A3;line-height:1.6}

/* bubbles */
.mtc-bwrap{display:flex;gap:9px;align-items:flex-end;animation:mtc-up .24s ease}
.mtc-bwrap.me{flex-direction:row-reverse}
.mtc-av{width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;
  font-size:.72rem;font-weight:700;flex-shrink:0;box-shadow:var(--shadow-1)}
.mtc-av-c{background:var(--accent);color:#fff}
.mtc-av-m{background:var(--brand);color:#fff}
.mtc-b{padding:10px 14px;border-radius:4px 14px 14px 14px;max-width:70%;font-size:.86rem;line-height:1.6;
  box-shadow:var(--shadow-1);word-break:break-word}
.mtc-b.me{border-radius:14px 4px 14px 14px}
.mtc-b-c{background:var(--accent);color:#fff}
.mtc-b-m{background:var(--surface);border:1px solid var(--line);color:#1E293B}
.mtc-btime{font-size:.64rem;color:var(--muted);margin-top:4px;display:block}

/* typing indicator */
.mtc-typing{display:flex;gap:4px;padding:6px 4px}
.mtc-typing span{width:6px;height:6px;border-radius:50%;background:rgba(255,255,255,.7);display:inline-block;
  animation:mtc-bounce 1.2s ease-in-out infinite}
.mtc-typing span:nth-child(2){animation-delay:.15s}
.mtc-typing span:nth-child(3){animation-delay:.3s}

.mtc-await{text-align:center;color:var(--muted);font-size:.82rem;padding:24px 0}

/* reply bar */
.mtc-reply{padding:12px 16px;border-top:1px solid var(--line);background:var(--surface);
  display:flex;gap:10px;align-items:center}
.mtc-reply-inp{flex:1;border:1px solid var(--line);border-radius:22px;padding:10px 18px;font-size:.88rem;
  font-family:var(--font);outline:none;color:var(--ink);transition:border-color .15s,box-shadow .15s;background:var(--bg)}
.mtc-reply-inp:focus{border-color:var(--focus);background:var(--surface);box-shadow:var(--ring)}
.mtc-reply-inp::placeholder{color:var(--muted)}
.mtc-send{width:40px;height:40px;border-radius:50%;border:none;background:var(--brand);color:#fff;
  display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;
  transition:transform .15s,background .18s;box-shadow:var(--shadow-1)}
.mtc-send:hover:not(:disabled){transform:scale(1.06);background:#000}
.mtc-send:active:not(:disabled){transform:scale(.96)}
.mtc-send:focus-visible{outline:none;box-shadow:var(--ring)}
.mtc-send:disabled{background:#E2E8F0;color:var(--muted);cursor:not-allowed}

/* empty / loader */
.mtc-empty{background:var(--surface);border:1px solid var(--line);border-radius:var(--radius-lg);
  padding:72px 24px;text-align:center;box-shadow:var(--shadow-1)}
.mtc-empty-ic{width:52px;height:52px;border-radius:14px;background:#F1F5F9;display:inline-flex;
  align-items:center;justify-content:center;color:var(--muted);margin-bottom:16px}
.mtc-empty-title{font-size:1rem;font-weight:700;margin-bottom:6px}
.mtc-empty-sub{font-size:.85rem;color:var(--muted)}
.mtc-loader{display:flex;align-items:center;justify-content:center;gap:12px;height:200px;color:var(--muted)}
.mtc-spin{width:28px;height:28px;border:3px solid var(--line);border-top-color:var(--accent);
  border-radius:50%;animation:mtc-spin .8s linear infinite}

@media(max-width:768px){
  .mtc-page{padding:72px 12px 100px !important} /* 72px = 56px top bar + 16px gap */
  .mtc-kpis{grid-template-columns:repeat(2,1fr) !important;gap:8px !important;margin-bottom:20px !important}
  .mtc-kpi{padding:12px 14px !important}
  .mtc-kpi-val{font-size:1.5rem !important;margin-top:4px !important}
  
  .mtc-row-head {
    grid-template-columns: 36px 1fr !important;
    min-height: auto !important;
    padding: 10px 0 !important;
  }
  .mtc-strip {
    width: 36px !important;
    border-right: none !important;
  }
  .mtc-right {
    grid-column: 2;
    padding: 4px 16px 0 16px !important;
    justify-content: flex-start !important;
    width: 100% !important;
  }
  .mtc-chev {
    display: none !important;
  }
  
  /* Bottom sheet modal for mobile */
  .mtc-modal-overlay {
    align-items: flex-end !important;
    padding: 0 !important;
  }
  .mtc-modal-card {
    max-width: 100% !important;
    width: 100% !important;
    border-radius: 20px 20px 0 0 !important;
    margin: 0 !important;
    padding-bottom: env(safe-area-inset-bottom, 20px) !important;
    animation: mtc-slide-up 0.3s cubic-bezier(0.22, 1, 0.36, 1) both !important;
  }
}

@keyframes mtc-slide-up {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

@keyframes mtc-spin{to{transform:rotate(360deg)}}
@keyframes mtc-bounce{0%,60%,100%{transform:translateY(0)}30%{transform:translateY(-5px)}}
@keyframes mtc-up{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
@keyframes mtc-expand{from{opacity:0}to{opacity:1}}

@media (prefers-reduced-motion:reduce){
  .mtc-root *{animation-duration:.001ms!important;animation-iteration-count:1!important;
    transition-duration:.001ms!important;scroll-behavior:auto!important}
}
`;

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export default function MesTicketsClient() {
  const { id } = useParams<{ id: string }>();
  const clientName = localStorage.getItem("ClientName") ?? "Client";
    const ClientId = localStorage.getItem("ClientId") ?? "IdClient";

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  /* chat state */
  const [activeId, setActiveId] = useState<number | null>(null);
  const [chatMsgs, setChatMsgs] = useState<ChatMsg[]>([]);
  const [chatTicket, setChatTicket] = useState<Ticket | null>(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  /* ── New Ticket State ── */
  const [showNewModal, setShowNewModal] = useState(false);
  const [newProb, setNewProb] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newSubmitting, setNewSubmitting] = useState(false);

  /* ── Fetch ticket list ── */
  const fetchTickets = useCallback(() => {
    if (!id) return;
    axios
      .get(`${process.env.REACT_APP_PHP_APP_URL}/client/${ClientId}/tickets`)
      .then(r => setTickets(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  /* ── Fetch chat messages ── */
  const fetchChat = useCallback(async (tid: number) => {
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_PHP_APP_URL}/ticket/${tid}/messages`
      );
      setChatTicket(r.data.ticket);
      setChatMsgs(r.data.messages ?? []);
    } catch { /* ignore poll errors */ }
  }, []);

  /* ── Open / close a ticket chat ── */
  const toggleTicket = (ticket: Ticket) => {
    if (activeId === ticket.id) {
      setActiveId(null);
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    setActiveId(ticket.id);
    setChatMsgs([]);
    setChatTicket(ticket);
    setChatLoading(true);
    fetchChat(ticket.id).finally(() => setChatLoading(false));
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => fetchChat(ticket.id), 10000);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  /* ── Auto-scroll ── */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMsgs, chatLoading]);

  /* ── Send reply ── */
  const handleSend = async () => {
    const text = replyText.trim();
    if (!text || sending || !activeId) return;
    setReplyText("");
    setSending(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/client/${id}/ticket/${activeId}/reply`,
        { message: text, clientName }
      );
      await fetchChat(activeId);
    } catch {
      setReplyText(text);
    } finally {
      setSending(false);
    }
  };

  /* ── Submit new ticket ── */
  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDesc.trim() || newSubmitting) return;
    setNewSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/client/${ClientId}/send-ticket`, {
        probleme: newProb || "Message de client",
        description: newDesc,
        clientName
      });
      setShowNewModal(false);
      setNewProb("");
      setNewDesc("");
      fetchTickets(); // Refresh list
    } catch (err) {
      console.error(err);
    } finally {
      setNewSubmitting(false);
    }
  };

  /* ── Derived data ── */
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    tickets.forEach(t => { c[t.Status] = (c[t.Status] || 0) + 1; });
    return c;
  }, [tickets]);

  const filtered = useMemo(
    () => (filter === "all" ? tickets : tickets.filter(t => t.Status === filter)),
    [tickets, filter]
  );

  const tabs = [
    { key: "all",    label: "Tous",   count: tickets.length },
    { key: "Non lu", label: "Non lu", count: counts["Non lu"] ?? 0 },
    { key: "Lu",     label: "Lu",     count: counts["Lu"] ?? 0 },
    { key: "Résolu", label: "Résolu", count: counts["Résolu"] ?? 0 },
    { key: "Fermé",  label: "Fermé",  count: counts["Fermé"] ?? 0 },
  ];

  const kpis = [
    { val: tickets.length,                              lbl: "Total",    accent: "#4F46E5" },
    { val: (counts["Non lu"] ?? 0) + (counts["Lu"] ?? 0), lbl: "En cours", accent: "#D97706" },
    { val: counts["Résolu"] ?? 0,                       lbl: "Résolus",  accent: "#059669" },
    { val: counts["Fermé"] ?? 0,                        lbl: "Fermés",   accent: "#64748B" },
  ];

  /* ════════════════ Render ══════════════════════════════ */
  return (
    <>
      <SidBarClient />
      <div className="mtc-root">
        <style>{CSS}</style>

        <div className="mtc-page">

          {/* Header */}
          <header className="mtc-hdr">
            <button className="mtc-back" onClick={() => window.history.back()} aria-label="Retour">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div style={{ flex: 1 }}>
              <h1 className="mtc-title">Mes tickets</h1>
              <p className="mtc-sub">Cliquez sur un ticket pour ouvrir le chat en direct</p>
            </div>
            <button
              onClick={() => setShowNewModal(true)}
              className="mtc-tab active"
              style={{ padding: '10px 16px', borderRadius: '12px' }}
            >
              <i className="bi bi-plus-lg" /> Nouveau Ticket
            </button>
          </header>

          {/* KPIs */}
          <section className="mtc-kpis" aria-label="Statistiques">
            {kpis.map(k => (
              <div className="mtc-kpi" key={k.lbl}>
                <div className="mtc-kpi-bar" style={{ background: k.accent }} />
                <div className="mtc-kpi-lbl">{k.lbl}</div>
                <div className="mtc-kpi-val" style={{ color: k.accent }}>{k.val}</div>
              </div>
            ))}
          </section>

          {/* Tabs */}
          <div className="mtc-tabs" role="tablist" aria-label="Filtrer les tickets">
            {tabs.map(tab => (
              <button
                key={tab.key}
                role="tab"
                aria-selected={filter === tab.key}
                className={`mtc-tab${filter === tab.key ? " active" : ""}`}
                onClick={() => setFilter(tab.key)}
              >
                {tab.label}
                {tab.count > 0 && <span className="mtc-badge">{tab.count}</span>}
              </button>
            ))}
          </div>

          {/* Content */}
          {loading ? (
            <div className="mtc-loader">
              <div className="mtc-spin" />
              <span style={{ fontSize: ".85rem" }}>Chargement…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="mtc-empty">
              <div className="mtc-empty-ic">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div className="mtc-empty-title">
                {filter === "all" ? "Aucun ticket" : `Aucun ticket « ${filter} »`}
              </div>
              <div className="mtc-empty-sub">
                {filter === "all"
                  ? "Vous n'avez pas encore soumis de demande."
                  : "Aucun ticket ne correspond à ce filtre."}
              </div>
            </div>
          ) : (
            <div className="mtc-list">
              {filtered.map(ticket => {
                const s = getS(ticket.Status);
                const isOpen = activeId === ticket.id;
                return (
                  <div key={ticket.id} className={`mtc-row${isOpen ? " active-row" : ""}`}>

                    {/* Row header */}
                    <div
                      className="mtc-row-head"
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      onClick={() => toggleTicket(ticket)}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleTicket(ticket); }
                      }}
                    >
                      <div className="mtc-strip" style={{ background: `${s.dot}14` }}>
                        <span className="mtc-dot" style={{ background: s.dot }} />
                      </div>
                      <div className="mtc-info">
                        <div className="mtc-prob">{ticket.Probleme || "Ticket support"}</div>
                        <div className="mtc-meta">
                          <span className="mtc-id">#{ticket.id}</span>
                          <span className="mtc-date">{fmt(ticket.Date)}</span>
                        </div>
                      </div>
                      <div className="mtc-right">
                        <span className="mtc-pill" style={{ background: s.pill, color: s.text }}>
                          <span className="mtc-pill-dot" style={{ background: s.dot }} />
                          {ticket.Status}
                        </span>
                        <svg className="mtc-chev" width="15" height="15" viewBox="0 0 24 24"
                          fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </div>
                    </div>

                    {/* Chat panel */}
                    {isOpen && (
                      <div className="mtc-chat">
                        <div className="mtc-chat-win">
                          {chatLoading ? (
                            <div className="mtc-loader" style={{ height: 120 }}>
                              <div className="mtc-spin" />
                            </div>
                          ) : (
                            <>
                              {/* Original message */}
                              <div>
                                <div className="mtc-init-lbl">Votre message initial</div>
                                <div className="mtc-init">{ticket.Description}</div>
                                <span className="mtc-btime" style={{ marginLeft: 4 }}>
                                  {fmt(ticket.Date)}
                                </span>
                              </div>

                              {chatMsgs.length === 0 && (
                                <div className="mtc-await">En attente de réponse du magasin…</div>
                              )}

                              {chatMsgs.map(msg => {
                                const isMe = msg.sender_type === "client";
                                return (
                                  <div key={msg.id} className={`mtc-bwrap${isMe ? " me" : ""}`}>
                                    <div className={`mtc-av ${isMe ? "mtc-av-c" : "mtc-av-m"}`}>
                                      {initial(msg.NamUtilisateur)}
                                    </div>
                                    <div>
                                      <div className={`mtc-b${isMe ? " me mtc-b-c" : " mtc-b-m"}`}>
                                        {msg.Message}
                                      </div>
                                      <span className="mtc-btime" style={{ textAlign: isMe ? "right" : "left" }}>
                                        {msg.NamUtilisateur} · {fmt(msg.Date)}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}

                              {sending && (
                                <div className="mtc-bwrap me">
                                  <div className="mtc-av mtc-av-c">{initial(clientName)}</div>
                                  <div className="mtc-b me mtc-b-c">
                                    <div className="mtc-typing">
                                      <span /><span /><span />
                                    </div>
                                  </div>
                                </div>
                              )}
                              <div ref={chatEndRef} />
                            </>
                          )}
                        </div>

                        {/* Reply input */}
                        <div className="mtc-reply">
                          <input
                            className="mtc-reply-inp"
                            placeholder="Écrire un message… (Entrée pour envoyer)"
                            aria-label="Votre message"
                            value={replyText}
                            onChange={e => setReplyText(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                          />
                          <button
                            className="mtc-send"
                            onClick={handleSend}
                            disabled={!replyText.trim() || sending}
                            aria-label="Envoyer le message"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          )}

        </div>
      </div>

      {/* ── New Ticket Modal ── */}
      {showNewModal && (
        <div className="mtc-modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15,23,42,0.6)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
          backdropFilter: 'blur(4px)', animation: 'mtc-expand .2s ease-out'
        }}>
          <div className="mtc-modal-card" style={{
            background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '500px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)', overflow: 'hidden'
          }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #E7EBF1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800 }}>Nouveau Ticket</h2>
              <button
                onClick={() => setShowNewModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.5rem', color: '#94A3B8' }}
              >&times;</button>
            </div>
            <form onSubmit={handleCreateTicket} style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, marginBottom: '8px', color: '#475569' }}>
                  Sujet / Problème (Optionnel)
                </label>
                <input
                  type="text"
                  value={newProb}
                  onChange={e => setNewProb(e.target.value)}
                  placeholder="Ex: Problème de livraison"
                  style={{ width: '100%', padding: '12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '0.9rem', outline: 'none' , color:"black" }}
                />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, marginBottom: '8px', color: '#475569' }}>
                  Message <span style={{ color: 'red' }}>*</span>
                </label>
                <textarea
                  required
                  value={newDesc}
                  onChange={e => setNewDesc(e.target.value)}
                  placeholder="Décrivez votre problème en détail..."
                  style={{ width: '100%', padding: '12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '0.9rem', minHeight: '120px', resize: 'vertical', outline: 'none' , color:"black"  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  style={{ padding: '10px 16px', border: '1px solid #CBD5E1', background: '#fff', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={newSubmitting || !newDesc.trim()}
                  style={{ padding: '10px 16px', border: 'none', background: '#0B1220', color: '#fff', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', opacity: (newSubmitting || !newDesc.trim()) ? 0.6 : 1 }}
                >
                  {newSubmitting ? 'Envoi...' : 'Envoyer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
